/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 내비게이션 항목 클래스를 정의한다.
 *
 * @file /src/components/navigation/elements/Item.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 30.
 */
import * as parent from '@base/Element';
import { Dragger } from '@utils/Dragger';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { DomList } from '@utils/DomList';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 항목명
     */
    title: string;
}

export abstract class Item extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type = 'navigationitem';

    /**
     * 항목명제목
     */
    title: string;

    /**
     * 항목명 DOM
     */
    $title: Dom = null;

    /**
     * 정렬버튼 DOM
     */
    $sorter: Dom = null;

    /**
     * 드래그 앤 드롭 상태
     *
     * @property scroller - 스크롤영역의 뷰포트 기준 절대위치
     * @property target - 스크롤영역 내 드래그대상의 현재 top 위치 (DOM 이동 또는 스크롤 시 초기화)
     * @property offset - 포인터와 드래그대상 상단 사이의 초기 오프셋
     * @property tracker - 드래거
     * @property onScroll - 스크롤 이벤트 핸들러 참조 (removeEvent 용)
     * @property $preview - 드래그 중 표시되는 프리뷰 DOM
     */
    drag: {
        scroller: DOMRect;
        target: number;
        offset: number;
        tracker: Dragger;
        onScroll: () => void;
        $preview: Dom;
    } = { scroller: null, target: null, offset: null, tracker: null, onScroll: null, $preview: null };

    /**
     * 항목 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Item> = null) {
        super(properties);

        this.title = this.properties.title;
    }

    /**
     * 항목명을 가져온다.
     *
     * @returns 항목명 DOM
     */
    $getTitle(): Dom {
        if (this.$title === null) {
            this.$title = Html.create('span', null, this.title);
        }

        return this.$title;
    }

    /**
     * 정렬을 위한 버튼을 가져온다.
     *
     * @returns 정렬버튼 DOM
     */
    $getSorter(): Dom {
        if (this.$sorter === null) {
            this.$sorter = Html.create('button', { type: 'button' }, '<i class="mi mi-bars"></i>');
            this.#initDragAndDrop();
        }

        return this.$sorter;
    }

    /**
     * 내비게이션 항목을 검색한다.
     *
     * @param keyword - 키워드
     * @returns 매칭여부
     */
    abstract search(keyword: string): boolean;

    /**
     * 드래그 앤 드롭 기능을 초기화한다.
     */
    #initDragAndDrop(): void {
        if (this.$sorter.getData('drag') !== null) {
            return;
        }

        const drag = new Dragger(this.$sorter, {
            cursor: 'ns-resize',
            listeners: {
                start: (_, tracker) => this.#handleDragStart(tracker),
                drag: () => this.#handleDrag(),
                end: () => this.#handleDragEnd(),
            },
        });

        this.$sorter.setData('drag', drag, false);
    }

    /**
     * 스크롤영역 내 드래그대상의 현재 top 위치를 가져온다.
     * DOM 이동 또는 스크롤 전까지 캐싱된 값을 재사용한다.
     *
     * @returns 드래그대상의 현재 위치
     */
    async #getDragTargetPosition(): Promise<number> {
        if (this.drag.target === null) {
            this.drag.scroller ??= await this.getComponent().getScroller().$target.getRect();
            const rect = await this.$element.getRect();
            this.drag.target = rect.top - this.drag.scroller.top;
        }

        return this.drag.target;
    }

    /**
     * 드롭이 될 수 있는 드래그대상의 이전/다음 대상을 가져온다.
     *
     * @param direction - up, down
     * @returns 드롭대상
     */
    #getDropTarget(direction: string): Dom | null {
        const variant = this.$element.getAttr('data-variant');

        let $drop = null;

        if (direction == 'up') {
            let $prevs: DomList = null;
            if (variant == 'page') {
                $prevs = Html.all(
                    'aui-element[data-type=navigationitem]:not(.drag)',
                    this.getComponent().$getContent()
                );
            } else {
                $prevs = Html.all(
                    'aui-element[data-type=navigationitem][data-variant=folder]:not(.drag)',
                    this.getComponent().$getContent()
                );
            }

            $prevs?.some(($item) => {
                if ($item.isEqual(this.$element) == true) {
                    return true;
                }
                $drop = $item;
            });
        } else {
            let $nexts: DomList = null;
            let found = false;

            if (variant == 'page') {
                $nexts = Html.all(
                    'aui-element[data-type=navigationitem]:not(.drag)',
                    this.getComponent().$getContent()
                );
            } else {
                $nexts = Html.all(
                    'aui-element[data-type=navigationitem][data-variant=folder]:not(.drag)',
                    this.getComponent().$getContent()
                );
            }

            $nexts?.some(($item) => {
                if (found == true) {
                    $drop = $item;
                    return true;
                }
                if ($item.isEqual(this.$element) == true) {
                    found = true;
                }
            });
        }

        if (variant == 'page' && $drop?.getAttr('data-variant') == 'folder') {
            $drop = Html.get('> b', $drop);
        }

        return $drop;
    }

    /**
     * 드래그 시작 시 호출되는 핸들러
     *
     * @param tracker - 드래그트래커
     */
    async #handleDragStart(tracker: Dragger): Promise<void> {
        this.drag.scroller = await this.getComponent().getScroller().$target.getRect();
        this.drag.tracker = tracker;
        this.drag.offset = tracker.getStart().y - this.drag.scroller.top - (await this.#getDragTargetPosition());
        this.drag.onScroll = () => this.#handleScroll();
        this.getComponent().getScroller().addEvent('active', this.drag.onScroll);

        const anchor = '--aui-nav-drag-' + Date.now();
        this.drag.$preview = this.$element.clone();
        this.drag.$preview.setStyle('position-anchor', anchor);
        this.drag.$preview.addClass('drag');
        this.$element.setStyle('anchor-name', anchor);
        this.$element.addClass('dragging');
        this.getComponent().$getContent().append(this.drag.$preview);
    }

    /**
     * 드래그 중일 때 호출되는 핸들러
     */
    async #handleDrag(): Promise<void> {
        if (this.drag.tracker === null || this.drag.$preview === null) {
            return;
        }

        const y = this.drag.tracker.getPosition().y;
        const move = y - this.drag.scroller.top - (await this.#getDragTargetPosition()) - this.drag.offset;

        const direction = move < 0 ? 'up' : 'down';
        const $drop = this.#getDropTarget(direction);

        /**
         * 이동할 대상이 없다면 프리뷰를 앵커위치로 고정하고, 과정을 중단한다.
         */
        if ($drop === null) {
            this.drag.$preview.setStyle('transform', `translateY(0px)`);
            return;
        }

        this.drag.$preview.setStyle('transform', `translateY(${move}px)`);

        if (y < this.drag.scroller.top + 80) {
            this.getComponent().getScroller().startScroll('y', -2);
        } else if (y > this.drag.scroller.bottom - 80) {
            this.getComponent().getScroller().startScroll('y', 2);
        } else {
            this.getComponent().getScroller().stopScroll('y');
        }

        const limit = $drop.getOuterHeight() / 2 + 10;
        if (Math.abs(move) <= limit) {
            return;
        }

        if ($drop.is('b') == true) {
            /**
             * 드롭대상이 폴더의 ROOT 인 경우
             */
            if (direction == 'up') {
                /**
                 * 드롭대상의 직전 DOM 이 폴더인지 확인한다.
                 */
                const $prev = $drop.getParent().prev() ?? null;
                if ($prev?.getAttr('data-variant') == 'folder') {
                    Html.get('div[data-role=folderitems]', $prev).append(this.$getElement());
                } else {
                    $drop.getParent().getParent().insertBefore(this.$getElement(), $drop.getParent());
                }
            } else {
                Html.get('div[data-role=folderitems]', $drop.getParent()).prepend(this.$getElement());
            }
        } else {
            if (direction == 'up') {
                $drop.getParent().insertBefore(this.$getElement(), $drop);
            } else {
                $drop.getParent().insertAfter(this.$getElement(), $drop);
            }
        }

        this.drag.target = null;
        this.#handleDrag();
    }

    /**
     * 드래그 중 자동스크롤이 진행중일때 호출되는 핸들러
     */
    #handleScroll(): void {
        this.drag.target = null;
        this.#handleDrag();
    }

    /**
     * 드래그 종료 시 호출되는 핸들러
     */
    #handleDragEnd(): void {
        const scroller = this.getComponent().getScroller();
        scroller.stopScroll('y');
        scroller.removeEvent('active', this.drag.onScroll);

        const $preview = this.drag.$preview;
        const $element = this.$element;

        this.drag = { scroller: null, target: null, offset: null, tracker: null, onScroll: null, $preview: null };

        if ($preview === null) {
            return;
        }

        $preview.animate({ transform: 'translateY(0)' }, { duration: 200, easing: 'ease-in-out' }, () => {
            $element.setStyle('anchor-name', null);
            $element.removeClass('dragging');
            $preview.remove();
        });
    }
}
